/**
 * One softphone per browser, however many CRM tabs are open.
 *
 * NicePhoneCallSync is mounted globally in Layout and renders NicePhoneToolbar,
 * so every open CRM tab loads the vendor widget and registers its own SIP
 * contact. The init guard in the toolbar is a module-level variable, which is
 * per JS context — it stops a double init inside one tab and does nothing
 * across tabs. Three things follow from that:
 *
 *   - several registrations share one AOR, so Asterisk forks an inbound call to
 *     all of them and the audio lands wherever it is answered first;
 *   - if the SIP peer caps contacts, the newest registration evicts an older
 *     one and the first tab silently stops receiving calls;
 *   - a tab that did not place the call sees callStatus CALLING rehydrated from
 *     localStorage with no SIP session of its own, and concludes the call is
 *     dead (see utils/zombieCall).
 *
 * The fix is to let exactly one tab own the phone. Leadership is a timestamped
 * claim in localStorage: the leader refreshes it, and any tab may take over once
 * it goes stale, so closing or crashing the leader hands the phone to another
 * tab rather than losing it.
 *
 * localStorage rather than BroadcastChannel or the Web Locks API because the
 * claim has to survive a crashed tab with no unload handler, and a timestamp
 * does that on its own.
 */

const KEY = 'relabix.phone.leader';

/** Refresh cadence for the leader, and how old a claim may be before it is up for grabs. */
export const HEARTBEAT_MS = 1500;
export const STALE_MS = 5000;

/**
 * Pure decision, so it can be tested without a browser.
 *
 * @param {object|null} record  the stored claim, or null
 * @param {string} selfId       this tab's id
 * @param {number} now          epoch ms
 * @returns {boolean} may this tab take or keep the phone
 */
export const shouldClaim = (record, selfId, now, staleMs = STALE_MS) => {
  if (!record || typeof record !== 'object') return true;      // nobody holds it
  if (record.tabId === selfId) return true;                    // already ours; refresh
  const age = now - Number(record.ts || 0);
  return !Number.isFinite(age) || age >= staleMs;              // previous leader went away
};

const readRecord = () => {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;   // private mode, blocked storage, corrupt value
  }
};

const writeRecord = (record) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
};

const newTabId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Start competing for the phone. Calls onChange(isLeader) whenever it flips.
 * Returns a stop function.
 *
 * When storage is unavailable the tab elects itself, so a single-tab user in a
 * locked-down browser keeps a working phone rather than none.
 */
export const startPhoneLeadership = (onChange) => {
  if (typeof window === 'undefined') return () => {};

  const selfId = newTabId();
  let leader = false;
  let timer = null;

  const publish = (next) => {
    if (next === leader) return;
    leader = next;
    try { onChange(leader); } catch { /* caller's problem, not ours */ }
  };

  const tick = () => {
    const record = readRecord();
    if (shouldClaim(record, selfId, Date.now())) {
      const ok = writeRecord({ tabId: selfId, ts: Date.now() });
      publish(ok || record === null);   // storage blocked → run the phone anyway
    } else {
      publish(false);
    }
  };

  const release = () => {
    const record = readRecord();
    if (record && record.tabId === selfId) {
      try { window.localStorage.removeItem(KEY); } catch { /* ignore */ }
    }
  };

  tick();
  timer = window.setInterval(tick, HEARTBEAT_MS);
  window.addEventListener('pagehide', release);

  return () => {
    if (timer) window.clearInterval(timer);
    window.removeEventListener('pagehide', release);
    release();
  };
};

export default { shouldClaim, startPhoneLeadership, HEARTBEAT_MS, STALE_MS };
